import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'

type AuthMode = 'signin' | 'signup' | 'forgot'

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    user,
    loading,
    error,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    requestPasswordReset,
    initialize,
    clearError,
  } = useAuthStore()

  const returnTo = searchParams.get('returnTo') || '/zinsser'

  const [mode, setMode] = useState<AuthMode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  // Initialize auth on mount
  useEffect(() => {
    initialize()
  }, [initialize])

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate(returnTo, { replace: true })
    }
  }, [user, navigate, returnTo])

  // Clear error and message when switching modes
  useEffect(() => {
    clearError()
    setMessage(null)
    setPassword('')
    setConfirmPassword('')
  }, [mode, clearError])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)

    if (mode === 'signin') {
      await signInWithEmail(email, password)
    } else if (mode === 'signup') {
      if (password !== confirmPassword) {
        return
      }
      await signUpWithEmail(email, password)
      if (!error) {
        setMessage('Check your email for a confirmation link')
      }
    } else if (mode === 'forgot') {
      await requestPasswordReset(email)
      if (!error) {
        setMessage('Check your email for a password reset link')
      }
    }
  }

  const isFormValid = () => {
    if (!email) return false
    if (mode === 'forgot') return true
    if (!password) return false
    if (mode === 'signup' && password !== confirmPassword) return false
    return true
  }

  return (
    <div className="h-dvh flex flex-col items-center justify-center bg-background px-6">
      {/* Logo / Brand */}
      <div className="mb-12 text-center">
        <h1 className="text-2xl font-light tracking-tight text-foreground">
          Zinsser
        </h1>
        <p className="mt-2 text-sm text-muted-foreground font-light">
          Your writing companion
        </p>
      </div>

      {/* Login Card */}
      <div className="w-full max-w-sm">
        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 border border-destructive/20 rounded-md bg-destructive/5">
            <p className="text-sm text-destructive">{error}</p>
            <button
              onClick={clearError}
              className="mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Success Message */}
        {message && (
          <div className="mb-6 p-4 border border-foreground/20 rounded-md bg-foreground/5">
            <p className="text-sm text-foreground">{message}</p>
          </div>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="sr-only">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              autoComplete="email"
              className="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm font-light focus:outline-none focus:ring-1 focus:ring-foreground"
              style={{ minHeight: '44px' }}
            />
          </div>

          {mode !== 'forgot' && (
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                className="w-full px-4 py-2.5 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm font-light focus:outline-none focus:ring-1 focus:ring-foreground"
                style={{ minHeight: '44px' }}
              />
            </div>
          )}

          {mode === 'signup' && (
            <div>
              <label htmlFor="confirmPassword" className="sr-only">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                autoComplete="new-password"
                className={`w-full px-4 py-2.5 rounded-md border bg-background text-foreground placeholder:text-muted-foreground text-sm font-light focus:outline-none focus:ring-1 focus:ring-foreground ${
                  confirmPassword && password !== confirmPassword
                    ? 'border-destructive'
                    : 'border-border'
                }`}
                style={{ minHeight: '44px' }}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-xs text-destructive">Passwords do not match</p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !isFormValid()}
            className="w-full flex items-center justify-center bg-foreground text-background hover:bg-foreground/90 px-4 py-2.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ minHeight: '44px' }}
          >
            <span className="text-sm font-light">
              {loading
                ? 'Please wait...'
                : mode === 'signin'
                  ? 'Sign in'
                  : mode === 'signup'
                    ? 'Create account'
                    : 'Send reset link'}
            </span>
          </button>
        </form>

        {/* Mode toggle links */}
        <div className="mt-4 text-center text-sm font-light text-muted-foreground">
          {mode === 'signin' && (
            <>
              <button
                onClick={() => setMode('forgot')}
                className="hover:text-foreground transition-colors"
              >
                Forgot password?
              </button>
              <span className="mx-2">·</span>
              <button
                onClick={() => setMode('signup')}
                className="hover:text-foreground transition-colors"
              >
                Create account
              </button>
            </>
          )}
          {mode === 'signup' && (
            <button
              onClick={() => setMode('signin')}
              className="hover:text-foreground transition-colors"
            >
              Already have an account? Sign in
            </button>
          )}
          {mode === 'forgot' && (
            <button
              onClick={() => setMode('signin')}
              className="hover:text-foreground transition-colors"
            >
              Back to sign in
            </button>
          )}
        </div>

        {/* Divider */}
        <div className="my-6 flex items-center gap-4">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground font-light">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Google Sign In Button */}
        <button
          onClick={signInWithGoogle}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 border border-border hover:bg-muted px-4 py-2.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ minHeight: '44px' }}
        >
          {/* Google Icon */}
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span className="text-sm font-light">Continue with Google</span>
        </button>

        {/* Terms */}
        <p className="mt-6 text-center text-xs text-muted-foreground font-light leading-relaxed">
          By continuing, you agree to our{' '}
          <a
            href="#"
            className="underline hover:text-foreground transition-colors"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="#"
            className="underline hover:text-foreground transition-colors"
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  )
}
